// File Manager delete confirmation copy (#473). Pure: given the rows about to be
// deleted it returns the title (names the file / folder, or counts the items) and
// the secondary line. The secondary line stays truthful about the retention
// policy: "Immediately" is a hard delete ("cannot be undone"), anything else is a
// soft delete to Recently Deleted (restorable), so no "cannot be undone" there.

export const NAME_MAX_CHARS = 48;

/** Shorten `name` to `max` chars by eliding the MIDDLE, keeping the extension visible. */
export function truncateMiddle(name, max = NAME_MAX_CHARS) {
    const s = String(name ?? '');
    if (s.length <= max) return s;
    const dot = s.lastIndexOf('.');
    const ext = dot > 0 && s.length - dot <= 8 ? s.slice(dot) : '';
    const stem = ext ? s.slice(0, dot) : s;
    const room = Math.max(4, max - ext.length - 1); // 1 for the ellipsis
    const head = Math.ceil(room * 0.6);
    const tail = room - head;
    return `${stem.slice(0, head)}…${tail > 0 ? stem.slice(stem.length - tail) : ''}${ext}`;
}

/**
 * @returns {{kind: 'file'|'folder'|'multi', name: string, count: number,
 *            titleKey: string, titleFallback: string, title: string,
 *            body: string, detail: string}}
 */
export function deleteDialogCopy({ documents = [], folders = [], immediate = true, t }) {
    const nFiles = documents.length;
    const nFolders = folders.length;
    const count = nFiles + nFolders;
    const tr = (key, fallback, vars) => t(key, fallback, vars);

    let kind; let name = ''; let titleKey; let titleFallback;
    if (count === 1 && nFiles === 1) {
        kind = 'file';
        name = truncateMiddle(documents[0].original_filename || documents[0].name || '');
        titleKey = 'fmDeleteFileTitle';
        titleFallback = "Delete '{name}'?";
    } else if (count === 1) {
        kind = 'folder';
        name = truncateMiddle(folders[0].name || '');
        titleKey = 'fmDeleteFolderTitle';
        titleFallback = "Delete folder '{name}' and all its contents?";
    } else {
        kind = 'multi';
        titleKey = 'fmDeleteItemsConfirm';
        titleFallback = 'Delete {n} item{s}?';
    }
    const vars = { name, n: count, s: count !== 1 ? 's' : '' };
    const title = tr(titleKey, titleFallback, vars);

    const hasFolders = nFolders > 0;
    let body;
    if (immediate) {
        body = hasFolders
            ? `${tr('fmDeleteCannotUndo', 'This action cannot be undone.')} ${tr('fmDeleteFoldersWarning', 'Folders and their contents will be permanently deleted.')}`
            : tr('fmDeleteCannotUndo', 'This action cannot be undone.');
    } else {
        body = tr('fmDeleteToTrash', 'Files will be moved to Recently Deleted, where you can restore them.');
    }

    const detail = kind === 'multi' && nFiles > 0 && nFolders > 0
        ? tr('fmDeleteBreakdown', '({files} files, {folders} folders)', { files: nFiles, folders: nFolders })
        : '';

    return { kind, name, count, titleKey, titleFallback, title, body, detail };
}
